import React from 'react';
import styled from 'styled-components';

const PresentationWrapper = styled.header`
  width: 100%;
  max-width: 100%;
  min-height: clamp(540px, 92vh, 760px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 6vw 48px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1100px;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: clamp(18px, 4vw, 40px);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Badge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.65);
  color: var(--muted);
  font-weight: 650;

  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: var(--accent);
    box-shadow: 0 0 0 4px rgba(38, 179, 168, 0.14);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2.1rem, 5.2vw + 0.4rem, 4rem);
  color: var(--text);
  line-height: 1.05;
  letter-spacing: -0.03em;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: clamp(1.05rem, 1.6vw + 0.55rem, 1.35rem);
  color: var(--muted);
  max-width: 70ch;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const PrimaryButton = styled.button`
  padding: 12px 16px;
  border: none;
  border-radius: 999px;
  background: var(--primary);
  color: white;
  font-weight: 750;
  letter-spacing: -0.01em;
  cursor: pointer;

  &:hover { background: var(--primaryHover); }
  &:focus-visible { outline: 3px solid rgba(38, 179, 168, 0.45); outline-offset: 3px; }
`;

const SecondaryButton = styled.button`
  padding: 12px 16px;
  border-radius: 999px;
  background: rgba(255,255,255,0.65);
  color: var(--text);
  border: 1px solid var(--border);
  font-weight: 750;
  cursor: pointer;

  &:hover { background: rgba(255,255,255,0.85); }
  &:focus-visible { outline: 3px solid rgba(38, 179, 168, 0.45); outline-offset: 3px; }
`;

const InfoCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 18px 18px;
`;

const InfoTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text);
`;

const MiniStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.55);
  border-radius: var(--radius-md);
  padding: 14px 14px;

  strong {
    display: block;
    font-size: 1.25rem;
    letter-spacing: -0.02em;
  }

  span {
    display: block;
    margin-top: 6px;
    color: var(--muted);
    line-height: 1.4;
  }
`;

function Presentation() {
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <PresentationWrapper>
      <Content>
        <Left>
          <Badge><i aria-hidden="true" /> Legislativas 2022 · Portugal</Badge>

          <Title>Voto desperdiçado: quando o teu voto não elege ninguém</Title>

          <Subtitle>
            Nas legislativas de 2022, cerca de <strong>13,48%</strong> dos votos não elegeram deputados.
            Aqui podes ver <strong>se isso acontece no teu círculo</strong> — e porquê.
          </Subtitle>

          <Actions>
            <PrimaryButton type="button" onClick={() => scrollToId('simulador')}>Experimentar o simulador</PrimaryButton>
            <SecondaryButton type="button" onClick={() => scrollToId('selecao')}>Explorar exemplos</SecondaryButton>
            <SecondaryButton type="button" onClick={() => scrollToId('visualizacao')}>Ver a visualização</SecondaryButton>
          </Actions>
        </Left>

        <Right>
          <InfoCard>
            <InfoTitle>Definição rápida</InfoTitle>
            <InfoText>
              Chamamos <strong>voto desperdiçado</strong> a um voto que, no teu círculo eleitoral, não contribui para eleger deputados
              (porque o partido não atinge representação nesse círculo).
            </InfoText>
          </InfoCard>

          <MiniStats>
            <Stat>
              <strong>~13,48%</strong>
              <span>dos votos sem mandato nas legislativas (2022)</span>
              <span style={{ marginTop: 4, fontSize: '0.9rem' }}>
                Fonte: resultados oficiais (CNE)
              </span>
            </Stat>
            <Stat>
              <strong>1 minuto</strong>
              <span>para simulares o teu círculo e partido</span>
            </Stat>
          </MiniStats>
        </Right>
      </Content>
    </PresentationWrapper>
  );
}

export default Presentation;
